import React, { useCallback, useEffect, useRef, useState } from "react";
import "./OptocinetiqueTexte.css";

interface OptocinetiqueTexteProps {
  text: string;
  wpm: number;
  caseSize: number;
  onScrollEnd?: () => void;
}

// Fonction pour mesurer la largeur d'un texte dans une police spécifique
const measureText = (text: string, fontSize: number, fontFamily: string): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (context) {
    context.font = `${fontSize}px ${fontFamily}`;
    return context.measureText(text).width;
  }
  return 0;
};

const OptocinetiqueTexte: React.FC<OptocinetiqueTexteProps> = ({
  text,
  wpm,
  caseSize,
  onScrollEnd,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [textWidth, setTextWidth] = useState<number>(0); // Largeur initiale du texte
  const [isRendered, setIsRendered] = useState(false);
  const [adjustedText, setAdjustedText] = useState<string>(text);
  const [initialTextWidth, setInitialTextWidth] = useState<number>(0); // Largeur du texte avant modification
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Stocke le timeout

  const cmParSeconde = (caseSize/4)*wpm;

  // Mesurer la taille réelle du texte une fois qu'il est rendu
  useEffect(() => {
    if (ref.current) {
      const currentTextWidth = measureText(text, 24, "Times New Roman"); // Largeur initiale du texte
      const spaceWidth = measureText("\u00A0", 24, "Times New Roman"); // Largeur d'un espace
      const containerWidth = ref.current.parentElement?.offsetWidth || 0;
      const containerWidthX3 = containerWidth * 2;

      // Stocker la largeur initiale du texte
      setInitialTextWidth(currentTextWidth);
      // Vérifie que la largeur du texte est inférieure à celle du conteneur
      if (currentTextWidth < containerWidthX3) {
        const spacesNeeded = Math.max(Math.ceil((containerWidthX3 - currentTextWidth) / spaceWidth), 0); // Nombre d'espaces nécessaire
        setAdjustedText(text + "\u00A0".repeat(spacesNeeded)); // Ajoute des espaces insécables
      } else {
        setAdjustedText(text); // Si le texte est suffisamment long, on ne modifie pas le texte
      }

      setTextWidth(currentTextWidth);
      setIsRendered(true);
    }
  }, [text, caseSize]);

  // Gérer la fin de l'animation
  const handleAnimationEnd = useCallback(() => {
    if (onScrollEnd) {
      onScrollEnd();
    }
  }, [onScrollEnd]);

  // Calcul de la distance et de la durée de l'animation une fois que le texte est mesuré
  useEffect(() => {
    if (isRendered && ref.current && textWidth > 0) {
      const el = ref.current; // capture la ref pour l'utiliser dans le cleanup
      const containerWidth = el.parentElement?.offsetWidth || 0;
      const containerWidthX3 = containerWidth * 2;
      const distanceToTravel = containerWidthX3 + textWidth; // Distance totale à parcourir
      const duration = distanceToTravel / cmParSeconde;
      const durationTheoric = (initialTextWidth + containerWidth) / cmParSeconde; // Durée théorique du défilement
      el.style.opacity = "1";
      el.style.animation = `scroll ${duration}s linear`;

      // Utiliser setTimeout pour déclencher onScrollEnd après la durée de l'animation
      timeoutRef.current = setTimeout(() => {
        handleAnimationEnd();
      }, durationTheoric * 1000); // Convertir la durée en millisecondes
      el.addEventListener("animationend", handleAnimationEnd);
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        el.removeEventListener("animationend", handleAnimationEnd);
      };
    }
  }, [text, textWidth, cmParSeconde, handleAnimationEnd, isRendered, initialTextWidth]);

  const widthReading = (25 * caseSize) / 4;

  return (
    <div className="bg-black flex items-center justify-center">
      <div
        className="scrollingTextContainer"
        style={{ width: `${widthReading}px`, height: "100vh" }}
      >
        <div
          ref={ref}
          className="scrollingText"
          onAnimationEnd={handleAnimationEnd}
        >
          {adjustedText}
        </div>
      </div>
    </div>
  );
};

export default OptocinetiqueTexte;
